import React from 'react'
import cn from 'classnames'
import {
  Button as BaseButton,
  ButtonProps as BaseButtonProps,
} from '@ariakit/react'
import Loader from '../Icon/Loader'

export const ButtonKinds = {
  ACCENT: 'accent',
  APPLE: 'apple',
  CONTROL: 'control',
  FACEBOOK: 'facebook',
  GOOGLE: 'google',
  LINK: 'link',
  LINKED_IN: 'linked-in',
  PLAY: 'play',
  MESSENGER: 'messenger',
  PAYPAL: 'paypal',
  PINTEREST: 'pinterest',
  PRIMARY: 'primary',
  /**
   * @deprecated Use `ACCENT`. Retained because certificates promo banners take
   * their button kind from GrowthBook payloads that still say 'primary-accent';
   * removable once no live flag emits that value.
   */
  PRIMARY_ACCENT: 'primary-accent',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary',
  TWITTER: 'twitter',
  UTILITY: 'utility',
} as const

export type ButtonKindValue = (typeof ButtonKinds)[keyof typeof ButtonKinds]

export const ButtonSizes = {
  XS: 'xs',
  SM: 'sm',
  MD: 'md',
  LG: 'lg',
  // deprecated
  SMALL: 'small',
  MEDIUM: 'medium',
} as const

export type ButtonSizeValue = (typeof ButtonSizes)[keyof typeof ButtonSizes]

const getTranslatedSize = (size: ButtonSizeValue) => {
  if (!Object.values(ButtonSizes).includes(size)) {
    return ButtonSizes.MD
  }

  switch (size) {
    case ButtonSizes.SMALL:
      return ButtonSizes.SM
    case ButtonSizes.MEDIUM:
      return ButtonSizes.MD
    default:
      return size
  }
}

export interface ButtonProps
  extends Omit<BaseButtonProps, 'as' | 'to' | 'href' | 'target' | 'focusable'> {
  // The Reakit-era props are declared as `never` rather than simply dropped so
  // that a leftover call site fails type-check instead of silently rendering
  // them as DOM attributes. `never` (not omission) is what catches props
  // arriving via a spread — JSX skips excess-property checks on spreads, so an
  // undeclared prop would slip through.
  /** @deprecated Removed. Use `render={<a href={…} />}` or `render={<Link to={…} />}`. */
  as?: never
  /** @deprecated Removed. Put `to` on the element passed to `render`. */
  to?: never
  /** @deprecated Removed. Put `href` on the element passed to `render`. */
  href?: never
  /** @deprecated Removed. Put `target` on the element passed to `render`. */
  target?: never
  /** @deprecated Removed. Use `accessibleWhenDisabled`. */
  focusable?: never
  /**
   * `isBasic` is a CustomLink prop. The Button only ever forwarded it down the
   * `as={Component}` path, so on a `render` Button it silently did nothing —
   * which is how OtherCategories lost its BasicLink in #68153. Put it on the
   * element passed to `render` instead.
   */
  isBasic?: never

  // Standard props
  css?: Record<string, string>
  fullWidth?: boolean
  kind?: ButtonKindValue
  loading?: boolean
  rounded?: boolean
  size?: ButtonSizeValue
  symmetrical?: boolean
  alwaysShowControls?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      fullWidth,
      kind = ButtonKinds.PRIMARY,
      loading,
      rounded,
      size = ButtonSizes.MD,
      symmetrical,
      render,
      ...props
    },
    ref,
  ) => {
    const translatedSize = getTranslatedSize(size)
    // Ariakit adds aria-disabled even beside native `disabled`, which ARIA in HTML says not to do.
    const rendersNativeButton =
      !render || (React.isValidElement(render) && render.type === 'button')
    const nativelyDisabled =
      rendersNativeButton &&
      props.disabled &&
      !props.accessibleWhenDisabled &&
      props['aria-disabled'] === undefined
    const classNames = cn({
      'c-button': true,
      'c-button--full-width': fullWidth,
      'c-button--loading': loading,
      'c-button--symmetrical': symmetrical,
      'c-button--symmetrical mc-corners--circle': rounded,
      [`c-button--${kind}`]: kind,
      [`c-button--${translatedSize}`]: translatedSize,
      [className ?? '']: className,
    })

    const buttonContent = (
      <>
        {!loading && children}
        {loading && (
          <>
            <span className='c-button__content'>{children}</span>
            <Loader className='c-button__loader' />
          </>
        )}
      </>
    )

    return (
      <BaseButton
        className={classNames}
        ref={ref}
        {...(render ? { render } : {})}
        {...props}
        {...(nativelyDisabled ? { 'aria-disabled': undefined } : {})}
      >
        {buttonContent}
      </BaseButton>
    )
  },
)

Button.displayName = 'Button'

export default Button
